import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { getBackendUrl } from '../config';

const BACKEND_URL = getBackendUrl();

export function SystemSettingsPage() {
  const { language, setLanguage, t } = useLanguage();
  const [theme, setTheme] = useState('dark');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('dio-theme') || 'dark';
    setTheme(saved);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('dio-theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(t('passwordMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(t('passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('passwordChangeError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
        <div className="max-w-lg mx-auto space-y-8">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{t('systemSettings')}</h1>

          <section className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">{t('languageLabel')}</h2>
            <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={() => setLanguage('sq')}
                className={`flex-1 py-3 text-sm font-semibold ${language === 'sq' ? 'bg-blue-600 text-white' : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                  }`}
              >
                Shqip
              </button>
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`flex-1 py-3 text-sm font-semibold ${language === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                  }`}
              >
                English
              </button>
            </div>
          </section>

          <section className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">{t('themeLabel')}</h2>
            <button
              type="button"
              onClick={toggleTheme}
              className="w-full flex items-center justify-between py-3 px-4 rounded-xl bg-gray-50 dark:bg-gray-700/80 text-gray-900 dark:text-white"
            >
              <span>{theme === 'dark' ? t('themeDark') : t('themeLight')}</span>
              <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">{t('toggleThemeLabel')}</span>
            </button>
          </section>

          <section className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">{t('changePassword')}</h2>
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('currentPassword')}</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('newPassword')}</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('confirmNewPassword')}</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  required
                  minLength={6}
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
              >
                {submitting ? t('saving') : t('save')}
              </button>
            </form>
          </section>
        </div>
      </div>
    </Layout>
  );
}

export function AboutPage() {
  const { t } = useLanguage();
  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('aboutApp')}</h1>
        <p className="text-gray-600 dark:text-gray-400 text-sm">DIO IOT · SCADA</p>
      </div>
    </Layout>
  );
}

export { ConnectionsPage } from './ConnectionsPage';
